import { useEffect } from "react";
import { LocalNotifications } from "@capacitor/local-notifications";
import { useAuth } from "../lib/auth";
import { markSlotEaten } from "../lib/queries";
import {
  ACTION_EATEN,
  registerMealActions,
  remindersSupported,
} from "../lib/mealReminderScheduler";
import type { MealSlot } from "../lib/types";

/**
 * Answers arriving from a meal-time notification.
 *
 * Mounted once at the root rather than on On Deck, because the answer can come
 * in while the app is anywhere — or closed. Tapping "Ate it" brings the app up
 * and delivers the action here, carrying the day and slot it was scheduled
 * for; nothing about the screen you happened to be on matters.
 *
 * The tick is all this does. Sending it to Health stays a deliberate act on
 * the board, which is what makes the Health data something you chose to write
 * rather than something a notification wrote for you.
 */
export function MealReminders() {
  const { user } = useAuth();

  useEffect(() => {
    if (!remindersSupported() || !user) return;
    let cancelled = false;
    const handles: Array<{ remove: () => Promise<void> }> = [];

    (async () => {
      await registerMealActions();
      try {
        const handle = await LocalNotifications.addListener(
          "localNotificationActionPerformed",
          (payload) => {
            if (cancelled) return;
            if (payload.actionId !== ACTION_EATEN) return;
            const extra = payload.notification.extra as
              | { date?: string; slot?: MealSlot }
              | undefined;
            if (!extra?.date || !extra?.slot) return;
            // Fire and forget: there's no UI to hold, and a failed write should
            // leave the meal unticked rather than claim otherwise.
            void markSlotEaten(user.uid, extra.date, extra.slot).catch(() => {});
          }
        );
        handles.push(handle);
      } catch {
        // Binary without the plugin — nothing to listen to.
      }
    })();

    return () => {
      cancelled = true;
      for (const h of handles) void h.remove().catch(() => {});
    };
  }, [user]);

  return null;
}
